import React from 'react';
import { QuotationRecord } from '@/types/domain/quotation-comparison';
import { formatInr } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { CheckCircle2, AlertTriangle, Trophy, Sparkles } from 'lucide-react';

interface QuotationMatrixTableProps {
  quotations: QuotationRecord[];
  onSelectSupplier: (quotation: QuotationRecord) => void;
}

export const QuotationMatrixTable: React.FC<QuotationMatrixTableProps> = ({
  quotations,
  onSelectSupplier,
}) => {
  if (quotations.length === 0) {
    return (
      <div className="p-16 text-center text-xs text-foreground-muted font-poppins">
        No supplier quotations available for commercial matrix comparison.
      </div>
    );
  }

  // Lowest evaluated cost for highlighting L1
  const lowestCost = Math.min(...quotations.map((q) => q.evaluatedCost));

  return (
    <div className="w-full overflow-x-auto rounded-2xl border border-border bg-card shadow-xs font-poppins custom-scrollbar">
      <table className="w-full text-left text-xs border-collapse">
        <thead>
          <tr className="bg-muted/80 backdrop-blur-sm border-b border-border">
            <th className="py-4 px-4 font-bold uppercase text-[11px] text-foreground-secondary w-72 min-w-[240px] sticky left-0 z-20 bg-muted/95 border-r border-border shadow-xs">
              Evaluation Attribute & Commercial Parameter
            </th>
            {quotations.map((q) => {
              const isLowest = q.evaluatedCost === lowestCost;
              return (
                <th
                  key={q.id}
                  className={`py-4 px-4 text-center border-l border-border min-w-[270px] space-y-1.5 ${
                    isLowest ? 'bg-emerald-500/5' : ''
                  }`}
                >
                  <div className="flex items-center justify-center gap-1.5">
                    {q.rank === 1 && <Trophy className="h-4 w-4 text-amber-500 fill-current shrink-0" />}
                    <span className="font-bold text-foreground text-sm truncate max-w-[200px]" title={q.vendorName}>
                      {q.vendorName}
                    </span>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-[10px] font-mono text-foreground-secondary">
                    <span>{q.quotationNumber}</span>
                    <span>•</span>
                    <span className="text-primary font-bold flex items-center gap-0.5">
                      <Sparkles className="h-3 w-3" /> Score: {q.overallScore}/100
                    </span>
                  </div>

                  {isLowest && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 shadow-2xs">
                      ★ Lowest Evaluated Bidder (L1)
                    </span>
                  )}

                  <div className="pt-1.5">
                    <Button
                      size="sm"
                      onClick={() => onSelectSupplier(q)}
                      className={`w-full rounded-xl font-bold font-poppins text-xs cursor-pointer shadow-xs ${
                        q.isSelected
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20'
                          : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                      }`}
                    >
                      {q.isSelected ? '✓ Sanctioned Supplier' : 'Award & Select Supplier'}
                    </Button>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {/* SECTION 1: COMMERCIAL FINANCIAL SUMMARY */}
          <tr className="bg-primary/10 font-bold text-primary uppercase text-[10px] tracking-wider">
            <td colSpan={quotations.length + 1} className="py-2.5 px-4 sticky left-0 z-10 bg-primary/10">
              1. Commercial Breakdown & Total Evaluated Cost (TCO)
            </td>
          </tr>

          <tr className="hover:bg-muted/30 transition-colors">
            <td className="py-3 px-4 font-bold text-foreground sticky left-0 z-10 bg-card border-r border-border">
              Base Quoted Subtotal
            </td>
            {quotations.map((q) => (
              <td key={q.id} className="py-3 px-4 text-center font-mono font-bold text-foreground border-l border-border/60">
                {formatInr(q.subtotalAmount)}
              </td>
            ))}
          </tr>

          <tr className="hover:bg-muted/30 transition-colors">
            <td className="py-3 px-4 font-bold text-foreground sticky left-0 z-10 bg-card border-r border-border">
              Commercial Sourcing Discount
            </td>
            {quotations.map((q) => (
              <td key={q.id} className="py-3 px-4 text-center font-mono text-emerald-600 dark:text-emerald-400 font-bold border-l border-border/60">
                -{formatInr(q.discountAmount)} ({q.commercialTerms.discountPercent}%)
              </td>
            ))}
          </tr>

          <tr className="hover:bg-muted/30 transition-colors">
            <td className="py-3 px-4 font-bold text-foreground sticky left-0 z-10 bg-card border-r border-border">
              Freight & Logistics Incoterms
            </td>
            {quotations.map((q) => (
              <td key={q.id} className="py-3 px-4 text-center font-mono font-medium text-foreground-secondary border-l border-border/60">
                +{formatInr(q.freightAmount)}
              </td>
            ))}
          </tr>

          <tr className="hover:bg-muted/30 transition-colors">
            <td className="py-3 px-4 font-bold text-foreground sticky left-0 z-10 bg-card border-r border-border">
              Statutory Taxes & GST (18%)
            </td>
            {quotations.map((q) => (
              <td key={q.id} className="py-3 px-4 text-center font-mono font-medium text-foreground-secondary border-l border-border/60">
                +{formatInr(q.taxAmount)}
              </td>
            ))}
          </tr>

          <tr className="bg-muted/40 font-extrabold border-t-2 border-b-2 border-border">
            <td className="py-4 px-4 text-foreground text-sm font-poppins sticky left-0 z-10 bg-muted/95 border-r border-border">
              Total Evaluated Cost (TCO)
            </td>
            {quotations.map((q) => {
              const isLowest = q.evaluatedCost === lowestCost;
              return (
                <td
                  key={q.id}
                  className={`py-4 px-4 text-center font-mono text-base border-l border-border/60 ${
                    isLowest
                      ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 font-bold'
                      : 'text-foreground font-bold'
                  }`}
                >
                  {formatInr(q.evaluatedCost)}
                  {isLowest && (
                    <span className="block text-[10px] font-poppins font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mt-0.5">
                      ★ Lowest TCO Bid (L1)
                    </span>
                  )}
                </td>
              );
            })}
          </tr>

          {/* SECTION 2: COMMERCIAL TERMS */}
          <tr className="bg-primary/10 font-bold text-primary uppercase text-[10px] tracking-wider">
            <td colSpan={quotations.length + 1} className="py-2.5 px-4 sticky left-0 z-10 bg-primary/10">
              2. Contractual Terms, Payment & Delivery Schedule
            </td>
          </tr>

          <tr className="hover:bg-muted/30 transition-colors">
            <td className="py-3 px-4 font-bold text-foreground sticky left-0 z-10 bg-card border-r border-border">
              Payment Terms
            </td>
            {quotations.map((q) => (
              <td key={q.id} className="py-3 px-4 text-center font-semibold text-foreground border-l border-border/60">
                {q.commercialTerms.paymentTerms}
              </td>
            ))}
          </tr>

          <tr className="hover:bg-muted/30 transition-colors">
            <td className="py-3 px-4 font-bold text-foreground sticky left-0 z-10 bg-card border-r border-border">
              Incoterms 2020
            </td>
            {quotations.map((q) => (
              <td key={q.id} className="py-3 px-4 text-center font-semibold text-foreground border-l border-border/60">
                {q.commercialTerms.incoterms}
              </td>
            ))}
          </tr>

          <tr className="hover:bg-muted/30 transition-colors">
            <td className="py-3 px-4 font-bold text-foreground sticky left-0 z-10 bg-card border-r border-border">
              Delivery Lead Time
            </td>
            {quotations.map((q) => (
              <td key={q.id} className="py-3 px-4 text-center font-mono font-bold text-foreground border-l border-border/60">
                {q.items[0]?.deliveryDays || 14} Calendar Days
              </td>
            ))}
          </tr>

          <tr className="hover:bg-muted/30 transition-colors">
            <td className="py-3 px-4 font-bold text-foreground sticky left-0 z-10 bg-card border-r border-border">
              OEM Warranty Coverage
            </td>
            {quotations.map((q) => (
              <td key={q.id} className="py-3 px-4 text-center font-semibold text-foreground border-l border-border/60">
                {q.commercialTerms.warrantyPeriod}
              </td>
            ))}
          </tr>

          {/* SECTION 3: TECHNICAL COMPLIANCE */}
          <tr className="bg-primary/10 font-bold text-primary uppercase text-[10px] tracking-wider">
            <td colSpan={quotations.length + 1} className="py-2.5 px-4 sticky left-0 z-10 bg-primary/10">
              3. Technical Compliance & Specification Audit
            </td>
          </tr>

          <tr className="hover:bg-muted/30 transition-colors">
            <td className="py-3 px-4 font-bold text-foreground sticky left-0 z-10 bg-card border-r border-border">
              Technical Specification Status
            </td>
            {quotations.map((q) => {
              const status = q.items[0]?.technicalCompliance || 'COMPLIANT';
              return (
                <td key={q.id} className="py-3 px-4 text-center border-l border-border/60">
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full ${
                      status === 'COMPLIANT'
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
                    }`}
                  >
                    {status === 'COMPLIANT' ? (
                      <CheckCircle2 className="h-3 w-3" />
                    ) : (
                      <AlertTriangle className="h-3 w-3" />
                    )}
                    {status.replace(/_/g, ' ')}
                  </span>
                </td>
              );
            })}
          </tr>

          {/* SECTION 4: LINE ITEM PRICE MATRIX */}
          <tr className="bg-primary/10 font-bold text-primary uppercase text-[10px] tracking-wider">
            <td colSpan={quotations.length + 1} className="py-2.5 px-4 sticky left-0 z-10 bg-primary/10">
              4. Line-Item Unit Price Comparison (INR ₹)
            </td>
          </tr>

          {quotations[0]?.items.map((item, idx) => (
            <tr key={item.id} className="hover:bg-muted/30 transition-colors">
              <td className="py-3 px-4 font-bold text-foreground space-y-0.5 sticky left-0 z-10 bg-card border-r border-border">
                <span className="block text-primary font-mono text-[11px]">Item #{idx + 1}: {item.materialCode}</span>
                <span className="block text-xs font-semibold text-foreground">{item.description}</span>
                <span className="block text-[10px] text-foreground-muted font-normal">
                  Scope Qty: {item.quantity} {item.uom}
                </span>
              </td>
              {quotations.map((q) => {
                const qItem = q.items.find((i) => i.materialCode === item.materialCode) || q.items[idx];
                return (
                  <td key={q.id} className="py-3 px-4 text-center border-l border-border/60">
                    <span className="font-mono font-bold text-foreground block text-xs">
                      {formatInr(qItem?.unitPrice || 0)} / {item.uom}
                    </span>
                    <span className="font-mono text-[10px] text-foreground-muted block mt-0.5">
                      Total: {formatInr(qItem?.totalPrice || 0)}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
